/**
 * The register DataTable: shadcn/ui table parts driven by TanStack Table v9.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * UX_SPEC section 2, register anatomy, in one component:
 * - toolbar: one search box over every column's plain-language text, the shown count,
 *   and a clear control once anything narrows the rows;
 * - every header sorts (click; shift-click adds a secondary sort, its rank shown);
 * - vocabulary columns filter through a multi-select menu with checkmarks that opens
 *   BELOW the header text and closes on outside click, Escape and scroll-away, with a
 *   count badge on the trigger while active;
 * - one viewport (max 70vh, overflow auto) with a sticky header;
 * - the footer (total, page, previous and next) OUTSIDE the viewport.
 *
 * A screen describes its columns as RegisterColumn records; the TanStack wiring lives
 * here once. The `text` of a column is what the cell SAYS in plain words: it is what the
 * search matches, what the filter lists, and (unless `sortValue` is given) what sorts.
 */
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ListFilter,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  columnFacetingFeature,
  columnFilteringFeature,
  createColumnHelper,
  createFacetedRowModel,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  tableFeatures,
  useTable,
  type Column,
  type ColumnDef,
  type FilterFn,
  type Row,
  type RowData,
  type TableFeatures,
} from "@tanstack/react-table";

import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Input } from "./input";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { strings } from "@/brand/strings";
import { cn } from "@/lib/utils";

const copy = strings.dataTable;

/** One register column, described in the screen's own terms. */
export interface RegisterColumn<TData extends RowData> {
  readonly id: string;
  readonly label: string;
  /** The cell's plain-language text: searched, filtered on, and sorted by default. */
  readonly text: (row: TData) => string;
  /** A sort key when the text does not order correctly (dates, numbers). */
  readonly sortValue?: (row: TData) => string | number;
  /** The rich cell (a link, a chip). Defaults to the text. */
  readonly render?: (row: TData) => React.ReactNode;
  /** A vocabulary column: offer the multi-select filter menu. */
  readonly filter?: boolean;
  /** Extra classes on the column's cells (width, wrapping). */
  readonly className?: string;
}

/** Multi-select membership: the row's text is one of the chosen values. */
const inSet: FilterFn<TableFeatures, RowData> = (row, columnId, filterValue) => {
  const chosen = Array.isArray(filterValue) ? (filterValue as unknown[]) : [];
  return chosen.length === 0 || chosen.includes(row.getValue<string>(columnId));
};
inSet.autoRemove = (value: unknown) => !Array.isArray(value) || value.length === 0;

/** The features every register uses, declared once (TanStack v9 registers them statically). */
const registerFeatures = tableFeatures({
  rowSortingFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  columnFacetingFeature,
  rowPaginationFeature,
  sortedRowModel: createSortedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  facetedRowModel: createFacetedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  paginatedRowModel: createPaginatedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric },
  filterFns: { includesString: filterFn_includesString, inSet },
});

type Features = typeof registerFeatures;

function compare(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function toColumnDefs<TData extends RowData>(
  columns: readonly RegisterColumn<TData>[],
): ColumnDef<Features, TData>[] {
  const helper = createColumnHelper<Features, TData>();
  return columns.map((one) => {
    const sortValue = one.sortValue;
    return helper.accessor((row: TData) => one.text(row), {
      id: one.id,
      header: one.label,
      cell: (context) => (one.render ? one.render(context.row.original) : context.getValue()),
      enableColumnFilter: one.filter === true,
      filterFn: "inSet",
      enableGlobalFilter: true,
      sortFn:
        sortValue === undefined
          ? "alphanumeric"
          : (a: Row<Features, TData>, b: Row<Features, TData>) =>
              compare(sortValue(a.original), sortValue(b.original)),
    }) as unknown as ColumnDef<Features, TData>;
  });
}

function ariaSort(direction: false | "asc" | "desc"): "ascending" | "descending" | "none" {
  return direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none";
}

/** The per-column filter menu: checkmarks, counts, opens below the header text. */
function HeaderFilter<TData extends RowData>({
  column,
  label,
  testId,
}: {
  readonly column: Column<Features, TData>;
  readonly label: string;
  readonly testId: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const raw: unknown = column.getFilterValue();
  const chosen = Array.isArray(raw) ? (raw as string[]) : [];
  const options = [...column.getFacetedUniqueValues().entries()]
    .map(([value, count]) => ({ value: String(value), count }))
    .sort((a, b) => compare(a.value, b.value));

  // Scroll-away closes the menu (UX_SPEC 2.7); outside click and Escape are Radix's.
  // "Away" means the HEADER moved: opening the menu moves focus, and a focus-driven
  // scroll that leaves the trigger where it was must not close what was just opened.
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) {
      return;
    }
    const start = trigger.current?.getBoundingClientRect() ?? null;
    const close = (): void => {
      const now = trigger.current?.getBoundingClientRect() ?? null;
      if (start === null || now === null) {
        return;
      }
      if (Math.abs(now.top - start.top) > 24 || Math.abs(now.left - start.left) > 24) {
        setOpen(false);
      }
    };
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  const toggle = (value: string): void => {
    const next = chosen.includes(value)
      ? chosen.filter((one) => one !== value)
      : [...chosen, value];
    column.setFilterValue(next.length === 0 ? undefined : next);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          ref={trigger}
          type="button"
          data-testid={testId}
          aria-label={copy.filterBy(label)}
          className={cn(
            "relative inline-flex size-8 items-center justify-center rounded-md border border-transparent text-muted-foreground pointer-coarse:size-11",
            "hover:border-rule-strong hover:text-foreground data-[state=open]:border-rule-strong",
            chosen.length > 0 && "text-gold",
          )}
        >
          <ListFilter className="size-4" aria-hidden="true" />
          {chosen.length > 0 && (
            <span
              className="absolute -end-1 -top-1 min-w-4 rounded-sm bg-gold px-1 text-[10px] leading-4 text-primary-foreground"
              aria-label={copy.filterActive(chosen.length)}
            >
              {chosen.length}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="start" data-testid={`${testId}-menu`}>
        <DropdownMenuLabel>{copy.filterBy(label)}</DropdownMenuLabel>
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={chosen.includes(option.value)}
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={() => {
              toggle(option.value);
            }}
          >
            <span className="flex-1">{option.value === "" ? copy.notSet : option.value}</span>
            <span className="text-xs text-muted-foreground tabular-nums">{option.count}</span>
          </DropdownMenuCheckboxItem>
        ))}
        {chosen.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                column.setFilterValue(undefined);
              }}
            >
              {copy.clearColumn}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DataTable<TData extends RowData>({
  data,
  columns,
  caption,
  testId,
  getRowId,
  getRowTestId,
  pageSize = 10,
  footer = true,
}: {
  readonly data: readonly TData[];
  readonly columns: readonly RegisterColumn<TData>[];
  /** The register's name, for assistive technology (the visible title is the card's). */
  readonly caption: string;
  /** Prefix for every test id: `${testId}-table`, `-search`, `-sort-<col>`, `-filter-<col>`. */
  readonly testId: string;
  readonly getRowId: (row: TData) => string;
  readonly getRowTestId?: ((row: TData) => string) | undefined;
  readonly pageSize?: number;
  /**
   * False when the rows are one page of the SERVER's paging (limit and offset): the
   * screen renders the server's pager beneath, and a second, client pager would lie.
   */
  readonly footer?: boolean;
}): React.JSX.Element {
  const columnDefs = useMemo(() => toColumnDefs(columns), [columns]);
  const table = useTable({
    features: registerFeatures,
    columns: columnDefs,
    data,
    getRowId: (row: TData) => getRowId(row),
    globalFilterFn: "includesString",
    initialState: { pagination: { pageIndex: 0, pageSize } },
  });

  const globalFilter = (table.state.globalFilter as string | undefined) ?? "";
  const filtered = table.getFilteredRowModel().rows.length;
  const narrowed = globalFilter !== "" || table.state.columnFilters.length > 0;
  const pageCount = Math.max(1, table.getPageCount());
  const page = table.state.pagination.pageIndex + 1;
  const rows = table.getRowModel().rows;

  return (
    <div className="q-ui flex min-w-0 flex-col gap-3" data-testid={`${testId}-register`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="relative w-full sm:w-80">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            value={globalFilter}
            aria-label={strings.common.searchLabel}
            placeholder={copy.searchPlaceholder}
            data-testid={`${testId}-search`}
            className="ps-9"
            onChange={(event) => {
              table.setGlobalFilter(event.target.value);
            }}
          />
        </div>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {copy.shown(filtered, data.length)}
        </p>
        {narrowed && (
          <Button
            variant="ghost"
            size="sm"
            data-testid={`${testId}-clear-filters`}
            onClick={() => {
              table.setGlobalFilter("");
              table.resetColumnFilters();
            }}
          >
            <X aria-hidden="true" />
            {copy.clearFilters}
          </Button>
        )}
        <p className="ms-auto hidden text-xs text-muted-foreground lg:block">
          {copy.multiSortHint}
        </p>
      </div>

      <div
        className="max-h-[70vh] overflow-auto rounded-md border border-rule bg-card"
        data-testid={`${testId}-viewport`}
      >
        <Table data-testid={`${testId}-table`}>
          <TableCaption>{caption}</TableCaption>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id} className="hover:bg-transparent">
                {group.headers.map((header) => {
                  const spec = columns.find((one) => one.id === header.column.id);
                  const label = spec?.label ?? header.column.id;
                  const sorted = header.column.getIsSorted();
                  const rank = header.column.getSortIndex();
                  const multi = table.state.sorting.length > 1;
                  const SortIcon =
                    sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;
                  return (
                    <TableHead
                      key={header.id}
                      aria-sort={ariaSort(sorted)}
                      data-testid={`${testId}-th-${header.column.id}`}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid={`${testId}-sort-${header.column.id}`}
                          aria-label={copy.sortBy(label)}
                          className={cn(
                            "-ms-2 inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 uppercase tracking-[0.08em] pointer-coarse:min-h-11",
                            "hover:bg-accent hover:text-foreground",
                            sorted !== false && "text-word",
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {label}
                          <SortIcon
                            aria-hidden="true"
                            className={cn("size-3.5", sorted === false && "opacity-50")}
                          />
                          {multi && rank >= 0 && (
                            <span className="text-[10px] tabular-nums">{rank + 1}</span>
                          )}
                        </button>
                        {header.column.getCanFilter() && (
                          <HeaderFilter
                            column={header.column}
                            label={label}
                            testId={`${testId}-filter-${header.column.id}`}
                          />
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columns.length}
                  className="py-8 text-center text-muted-foreground"
                  data-testid={`${testId}-no-matches`}
                >
                  {copy.noMatches}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  {...(getRowTestId !== undefined
                    ? { "data-testid": getRowTestId(row.original) }
                    : {})}
                >
                  {row.getAllCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={columns.find((one) => one.id === cell.column.id)?.className}
                    >
                      <table.FlexRender cell={cell} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {footer && (
        <div className="flex flex-wrap items-center gap-3" data-testid={`${testId}-footer`}>
          <p className="text-sm text-muted-foreground" data-testid={`${testId}-page`}>
            {copy.footer(strings.common.total(filtered), strings.common.page(page, pageCount))}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid={`${testId}-prev`}
              disabled={!table.getCanPreviousPage()}
              onClick={() => {
                table.previousPage();
              }}
            >
              <ChevronLeft aria-hidden="true" />
              {strings.common.previousPage}
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid={`${testId}-next`}
              disabled={!table.getCanNextPage()}
              onClick={() => {
                table.nextPage();
              }}
            >
              {strings.common.nextPage}
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
